import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function FAQPage() {
  const { getTotalItems } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'ri-question-line' },
    { id: 'orders', name: 'Orders & Delivery', icon: 'ri-shopping-cart-line' },
    { id: 'payment', name: 'Payment & Billing', icon: 'ri-secure-payment-line' },
    { id: 'codes', name: 'Code Activation', icon: 'ri-key-line' },
    { id: 'account', name: 'Account & Security', icon: 'ri-user-line' },
    { id: 'technical', name: 'Technical Issues', icon: 'ri-tools-line' }
  ];

  const faqs = [
    {
      id: 1,
      category: 'orders',
      question: 'How long does it take to receive my code?',
      answer: 'Most codes are delivered instantly within 30 seconds of successful payment. In rare cases, it may take up to 5 minutes. If you haven\'t received your code after 5 minutes, please contact our support team.'
    },
    {
      id: 2,
      category: 'orders',
      question: 'What should I do if I haven\'t received my code?',
      answer: 'First, check your email spam/junk folder. If the code isn\'t there, log into your account and check your order history. If you still can\'t find it, contact our 24/7 support team with your order number.'
    },
    {
      id: 3,
      category: 'payment',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, Apple Pay, Google Pay, and various regional payment methods depending on your location.'
    },
    {
      id: 4,
      category: 'payment',
      question: 'Is it safe to make payments on your website?',
      answer: 'Yes, absolutely. We use industry-standard SSL encryption and are PCI DSS compliant. All payment information is processed securely through trusted payment processors. We never store your payment details on our servers.'
    },
    {
      id: 5,
      category: 'payment',
      question: 'Can I get a refund?',
      answer: 'Refunds are available within 24 hours of purchase if the code hasn\'t been redeemed. Once a code is activated, refunds are not possible due to the digital nature of the product. Contact support for refund requests.'
    },
    {
      id: 6,
      category: 'codes',
      question: 'My Fortnite code isn\'t working. What should I do?',
      answer: 'Ensure you\'re redeeming the code on the correct platform (Epic Games Store, PlayStation, Xbox, etc.). Check that the code is for your region. If it still doesn\'t work, contact support with your order details and error message.'
    },
    {
      id: 7,
      category: 'codes',
      question: 'Can I use codes from different regions?',
      answer: 'Most codes are region-locked. Make sure to purchase codes that match your account\'s region. We clearly indicate the region for each product. If you\'re unsure about compatibility, contact support before purchasing.'
    },
    {
      id: 8,
      category: 'codes',
      question: 'How do I redeem my V-Bucks code?',
      answer: 'Log into your Epic Games account, go to the "Redeem Code" section, enter your code, and click redeem. V-Bucks will be added to your account immediately. You can also redeem through your gaming console\'s store.'
    },
    {
      id: 9,
      category: 'account',
      question: 'Do I need to create an account to make a purchase?',
      answer: 'No, you can checkout as a guest. However, creating an account allows you to track orders, view purchase history, and receive exclusive offers. We recommend creating an account for the best experience.'
    },
    {
      id: 10,
      category: 'account',
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the login page, enter your email address, and we\'ll send you a password reset link. If you don\'t receive the email within 5 minutes, check your spam folder or contact support.'
    },
    {
      id: 11,
      category: 'technical',
      question: 'The website is loading slowly. What can I do?',
      answer: 'Try clearing your browser cache and cookies, disable browser extensions, or try a different browser. If the issue persists, it might be a temporary server issue. Contact support if problems continue.'
    },
    {
      id: 12,
      category: 'technical',
      question: 'I\'m getting an error during checkout. Help!',
      answer: 'Common checkout errors are usually payment-related. Ensure your payment details are correct, your card has sufficient funds, and isn\'t blocked for international transactions. Try a different payment method or contact support.'
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/3592b2bbca46534dcc53ae1aa674a0a8.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Frequently Asked <span className="text-yellow-400">Questions</span>
            </h1>
            <p className="text-xl text-purple-200 mb-8 max-w-3xl mx-auto">
              Find quick answers to common questions about orders, payments, codes, and more.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-sm text-white px-6 py-4 pl-12 rounded-lg border border-white/20 focus:border-purple-400 focus:outline-none text-sm placeholder-white/60"
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <i className={`${category.icon} text-sm`}></i>
                <span className="text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-line text-gray-400 text-3xl"></i>
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">No results found</h3>
              <p className="text-gray-400">Try adjusting your search terms or browse different categories.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div key={faq.id} className="bg-gray-800 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    <h3 className="text-white font-semibold pr-4">{faq.question}</h3>
                    <i className={`ri-arrow-${openFAQ === faq.id ? 'up' : 'down'}-s-line text-purple-400 text-xl flex-shrink-0`}></i>
                  </button>
                  
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-4 border-t border-gray-700">
                      <p className="text-gray-300 pt-4 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Still Need Help?</h2>
          <p className="text-gray-300 mb-8">
            Can't find the answer you're looking for? Our support team is here to help you 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="inline-block">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-customer-service-line mr-2"></i>
                Contact Support
              </button>
            </a>
            <a href="/support" className="inline-block">
              <button className="border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-question-line mr-2"></i>
                Support Center
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Popular Help Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Code Delivery</h3>
              <p className="text-gray-400 text-sm">Learn about our instant delivery system and what to do if codes are delayed.</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Security</h3>
              <p className="text-gray-400 text-sm">Information about our security measures and how we protect your data.</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refund-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Refunds</h3>
              <p className="text-gray-400 text-sm">Understand our refund policy and how to request a refund if needed.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}